import { LinearGradient } from 'expo-linear-gradient';
import React, { forwardRef, Ref } from 'react';
import {
  ButtonProps as RNButtonProps,
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  ViewStyle,
  TextStyle,
} from 'react-native';

import generateID from '../../utils/generate-id';

type ButtonProps = {
  accessibilityLabel?: string;
  id?: string;
  disabled?: boolean;
  onPress: RNButtonProps['onPress'];
  style?: StyleProp<ViewStyle>;
  title: string;
  titleStyle?: StyleProp<TextStyle>;
  variant?: 'primary' | 'dark' | 'danger' | 'outlined' | 'text';
};

const Button = forwardRef((props: ButtonProps, ref: Ref<TouchableOpacity>) => {
  const isPrimary = !props.variant || props.variant === 'primary';
  const isDark = props.variant === 'dark';
  const isDanger = props.variant === 'danger';
  const isOutlined = props.variant === 'outlined';
  const isText = props.variant === 'text';

  return (
    <TouchableOpacity
      ref={ref}
      /** props */
      accessibilityLabel={props.accessibilityLabel}
      disabled={props.disabled}
      onPress={props.onPress}
      /** style */
      style={[
        style.button,
        !isPrimary && style.buttonVariantNonPrimary,
        isDark && style.buttonDark,
        isDanger && style.buttonDanger,
        isOutlined && style.buttonOutlined,
        // outlined and text variant share the same appearance,
        // except for the border line for outlined
        isText && [style.buttonOutlined, style.buttonText],
        props.disabled && [
          !isOutlined && !isText && style.buttonVariantNonPrimary,
          !isOutlined && !isText && style.buttonDisabled,
          (isOutlined || isText) && style.buttonOutlinedDisabled,
          isText && style.buttonTextDisabled,
        ],
        props.style,
      ]}
      /** Generate HTML and Test IDs  */
      {...generateID(props.id)}
      /** Platform-specific props */
      {...Platform.select({
        web: {
          accessibilityRole: 'button',
          accessibilityDisabled: props.disabled,
        },
      })}
    >
      {(!props.variant || props.variant === 'primary') && !props.disabled ? (
        <LinearGradient
          colors={['#E5258C', '#823CD7']}
          /** will do an approximately 135-degree gradient */
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 6 }}
          style={style.gradient}
        >
          <Text style={[style.title, props.titleStyle]}>{props.title}</Text>
        </LinearGradient>
      ) : (
        <Text
          style={[
            style.title,
            // outlined and text variant share the same appearance,
            // except for the border line for outlined
            (isOutlined || isText) && style.titleOutlined,
            props.disabled &&
              (isOutlined || isText) &&
              style.titleOutlinedDisabled,
            props.titleStyle,
          ]}
        >
          {props.title}
        </Text>
      )}
    </TouchableOpacity>
  );
});

const style = StyleSheet.create({
  button: {
    borderRadius: 32,
    overflow: 'hidden',
    height: 56,
    width: '100%',
  },
  buttonVariantNonPrimary: {
    justifyContent: 'center',
    alignContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  buttonDark: {
    backgroundColor: '#2A2E32',
  },
  buttonDanger: {
    backgroundColor: '#FF2751',
  },
  buttonOutlined: {
    backgroundColor: 'transparent',
    borderColor: '#2A2E32',
    borderWidth: 2,
  },
  buttonText: {
    borderColor: 'transparent',
  },
  buttonDisabled: {
    backgroundColor: '#CFD4D8',
  },
  buttonOutlinedDisabled: {
    borderColor: '#CFD4D8',
  },
  buttonTextDisabled: {
    borderColor: 'transparent',
  },
  gradient: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  title: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  titleOutlined: {
    color: '#2A2E32',
  },
  titleOutlinedDisabled: {
    color: '#CFD4D8',
  },
});

export default Button;
export { RNButtonProps };
